import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  COOKIE_PREFS_EVENT,
  getConsent,
  saveConsent,
} from "@/lib/cookieConsent";
import { withLang } from "@/lib/langPath";

const Toggle = ({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative h-6 w-11 shrink-0 border transition-colors ${
      checked ? "border-black bg-black" : "border-black/40 bg-transparent"
    } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
  >
    <span
      className={`absolute top-0.5 h-4 w-4 transition-all ${
        checked ? "left-6 bg-bone" : "left-0.5 bg-black/60"
      }`}
    />
  </button>
);

const CookieConsent = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
    setAnalytics(consent.analytics);
    setMarketing(consent.marketing);
  }, []);

  useEffect(() => {
    const handler = () => {
      const consent = getConsent();
      setAnalytics(consent?.analytics ?? false);
      setMarketing(consent?.marketing ?? false);
      setShowPrefs(true);
      setIsVisible(true);
    };
    window.addEventListener(COOKIE_PREFS_EVENT, handler);
    return () => window.removeEventListener(COOKIE_PREFS_EVENT, handler);
  }, []);

  const persist = (a: boolean, m: boolean) => {
    saveConsent({ analytics: a, marketing: m });
    setAnalytics(a);
    setMarketing(m);
    setIsVisible(false);
    setShowPrefs(false);
  };

  const categories: { key: "necessary" | "analytics" | "marketing"; checked: boolean; locked?: boolean; set?: (v: boolean) => void }[] = [
    { key: "necessary", checked: true, locked: true },
    { key: "analytics", checked: analytics, set: setAnalytics },
    { key: "marketing", checked: marketing, set: setMarketing },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed bottom-0 left-0 right-0 z-50"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          <div className="w-full">
            <div className="relative bg-bone shadow-2xl p-4 md:p-8 max-h-[85vh] overflow-y-auto">
              <button
                onClick={() => persist(false, false)}
                className="absolute top-4 right-4 p-2 text-black/60 hover:text-black transition-colors"
                aria-label={t("sChrome.cookieConsent.close")}
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex flex-col md:flex-row items-start md:items-end gap-4 md:gap-6 md:pr-10">
                <div className="flex-1 pr-8 md:pr-0">
                  <h3 className="text-lg md:text-3xl font-normal text-black mb-1 md:mb-2">
                    {t("sChrome.cookieConsent.title")}
                  </h3>
                  <p className="text-sm md:text-lg text-black/70 leading-snug md:leading-relaxed">
                    {t("sChrome.cookieConsent.body")}{" "}
                    <Link
                      to={withLang("/cookies")}
                      className="text-black underline underline-offset-4 font-medium"
                    >
                      {t("sChrome.cookieConsent.viewPolicy")}
                    </Link>
                  </p>
                </div>

                <div className="grid grid-cols-3 md:flex md:flex-wrap items-end gap-2 md:gap-3 w-full md:w-auto md:self-end">
                  <button
                    onClick={() => setShowPrefs((v) => !v)}
                    className="md:flex-none border border-black/40 px-2 md:px-6 py-2.5 md:py-3 text-sm md:text-base font-medium text-black transition-colors hover:bg-black/10"
                  >
                    {t("sChrome.cookieConsent.configure")}
                  </button>
                  <button
                    onClick={() => persist(false, false)}
                    className="md:flex-none border border-black/40 px-2 md:px-6 py-2.5 md:py-3 text-sm md:text-base font-medium text-black transition-colors hover:bg-black/10"
                  >
                    {t("sChrome.cookieConsent.reject")}
                  </button>
                  <button
                    onClick={() => persist(true, true)}
                    className="md:flex-none bg-black px-2 md:px-6 py-2.5 md:py-3 text-sm md:text-base font-medium text-bone transition-opacity hover:opacity-80"
                  >
                    {t("sChrome.cookieConsent.accept")}
                  </button>
                </div>
              </div>

              <AnimatePresence initial={false}>
                {showPrefs && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-8 border-t border-black/10 pt-6">
                      <div className="flex flex-col gap-5">
                        {categories.map((c) => (
                          <div key={c.key} className="flex items-start gap-4">
                            <Toggle
                              checked={c.checked}
                              disabled={c.locked}
                              onChange={(v) => c.set?.(v)}
                              label={t(`sChrome.cookieConsent.categories.${c.key}.title`)}
                            />
                            <div>
                              <p className="text-base font-medium text-black">
                                {t(`sChrome.cookieConsent.categories.${c.key}.title`)}
                                {c.locked && (
                                  <span className="ml-2 text-xs uppercase tracking-widest text-black/50">
                                    {t("sChrome.cookieConsent.always")}
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-black/60 leading-relaxed">
                                {t(`sChrome.cookieConsent.categories.${c.key}.desc`)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => persist(analytics, marketing)}
                        className="mt-6 w-full md:w-auto bg-black px-6 py-3 text-base font-medium text-bone transition-opacity hover:opacity-80"
                      >
                        {t("sChrome.cookieConsent.save")}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
